import type { Answers } from "../../../../../../../features/questionnaire/types";
import type { Options } from "../../../../../../../components/Questionnaire/Section/Question/QuestionFactory/types";
import type { QuestionProps } from "../../../../../../../components/Questionnaire/Section/Question/types";
import { isNoCoverValue } from "../../../../../../../components/Questionnaire/Section/Question/QuestionFactory/questions/PersonalisedCoverQuestion/constants";
import {
  getOffValue,
  getDefaultOnValue,
  isMandatoryCover,
} from "../../../../../../../components/Questionnaire/Section/Question/QuestionFactory/questions/PersonalisedCoverQuestion/utils";
import { getPrimaryAnswer } from "../../utils/helpers";

/**
 * Calculates the prepopulate value for a cover question
 * - Mandatory covers (no off option): use first valid cover option
 * - Non-mandatory covers (has off option): use off value
 */
export const getPrepopulateValue = (options: Options | undefined): string => {
  const offValue = getOffValue(options);

  if (isMandatoryCover(options)) {
    return getDefaultOnValue(options, offValue);
  }

  return offValue;
};

/**
 * Determines if a cover question needs prepopulation based on current value and cover type.
 * - undefined, null, or "" always needs prepopulation
 * - "0", "no" only need prepopulation for mandatory covers (to replace invalid off value)
 */
export const needsPrepopulation = (
  currentValue: unknown,
  options: Options | undefined,
): boolean => {
  if (
    currentValue === undefined ||
    currentValue === null ||
    currentValue === ""
  ) {
    return true;
  }

  const normalisedValue =
    typeof currentValue === "number" || typeof currentValue === "boolean"
      ? String(currentValue)
      : currentValue;

  if (isMandatoryCover(options) && isNoCoverValue(normalisedValue)) {
    return true;
  }

  return false;
};

/**
 * Calculate primary answer with prepopulation if needed
 */
export const getPrimaryAnswerWithPrepopulation = (
  primaryQuestion: QuestionProps,
  answers: Answers,
): string | null => {
  const existing = getPrimaryAnswer(primaryQuestion, answers);

  return needsPrepopulation(existing, primaryQuestion.options)
    ? getPrepopulateValue(primaryQuestion.options)
    : existing;
};
